import { Button, Checkbox, RefSelectProps, Select } from "antd";
import { t } from "i18next";
import React from "react";
import { ZUid } from "src/types/zUid";
import styles from "./StateFilter.module.less";

export interface StateFilterProps {
  options: { label: string; value: ZUid }[];
  value: ZUid[];
  onApply: (value: ZUid[]) => void;
  disabled?: boolean;
}

const normalizeIds = (ids: ZUid[]) => Array.from(new Set(ids)).sort();

export const StateFilter: React.FC<StateFilterProps> = ({
  options,
  value,
  onApply,
  disabled = false,
}) => {
  // null means closed; an array is the selection being edited before OK.
  const [draft, setDraft] = React.useState<ZUid[] | null>(null);
  const selectRef = React.useRef<RefSelectProps>(null);
  const applied = normalizeIds(value);
  const appliedKey = JSON.stringify(applied);
  const countLabel = applied.length
    ? t("Selected states count", { count: applied.length })
    : "";
  const selected = draft ?? applied;
  const availableIds = options.map((option) => option.value);
  const selectedCount = availableIds.filter((id) =>
    selected.includes(id),
  ).length;
  const allSelected = options.length > 0 && selectedCount === options.length;
  const missingOptions = applied
    .filter((id) => !availableIds.includes(id))
    .map((id) => ({ value: id, label: t("Unavailable state", { id }) }));

  React.useEffect(() => {
    setDraft(null);
  }, [appliedKey, disabled]);

  const close = () => {
    setDraft(null);
    selectRef.current?.focus();
  };

  const renderDropdown = (menu: React.ReactNode) => (
    <>
      {menu}
      <div
        role="presentation"
        className={styles.actions}
        onMouseDown={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Escape") close();
        }}
      >
        <Checkbox
          checked={allSelected}
          indeterminate={selectedCount > 0 && !allSelected}
          disabled={options.length === 0}
          onChange={({ target }) => {
            const unavailable = selected.filter(
              (id) => !availableIds.includes(id),
            );
            setDraft(
              target.checked ? [...unavailable, ...availableIds] : unavailable,
            );
          }}
        >
          {t("Select all states")}
        </Checkbox>
        <Button
          type="primary"
          onClick={() => {
            onApply(normalizeIds(selected));
            close();
          }}
        >
          {t("OK")}
        </Button>
      </div>
    </>
  );

  return (
    <Select<ZUid[]>
      ref={selectRef}
      className={styles.select}
      aria-label={t("State")}
      mode="multiple"
      showSearch={false}
      disabled={disabled}
      options={[...options, ...missingOptions]}
      value={selected}
      open={draft !== null}
      maxTagCount={0}
      maxTagPlaceholder={countLabel}
      placeholder={countLabel}
      onChange={(ids) => {
        if (draft !== null) setDraft(ids);
      }}
      onDropdownVisibleChange={(open) => setDraft(open ? applied : null)}
      dropdownRender={renderDropdown}
    />
  );
};
